import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';

const STATUS_COLORS = {
     new: 'bg-blue-500/20 text-blue-300',
     contacted: 'bg-orange-500/20 text-orange-300',
     qualified: 'bg-purple-500/20 text-purple-300',
     proposal_sent: 'bg-yellow/20 text-yellow',
     won: 'bg-green-500/20 text-green-300',
     lost: 'bg-red-500/20 text-red-300',
};

const STATUS_LABELS = {
     new: 'New',
     contacted: 'Contacted',
     qualified: 'Qualified',
     proposal_sent: 'Proposal Sent',
     won: 'Won',
     lost: 'Lost',
};

const STATUSES = ['all', 'new', 'contacted', 'qualified', 'proposal_sent', 'won', 'lost'];

export default function InquiriesList() {
     const [inquiries, setInquiries] = useState([]);
     const [search, setSearch] = useState('');
     const [statusFilter, setStatusFilter] = useState('all');
     const [loading, setLoading] = useState(true);

     useEffect(() => {
          fetchInquiries();
     }, []);

     async function fetchInquiries() {
          const { data } = await supabase
               .from('inquiries')
               .select('*')
               .order('created_at', { ascending: false });
          setInquiries(data || []);
          setLoading(false);
     }

     const filtered = inquiries.filter((inq) => {
          if (statusFilter !== 'all' && inq.status !== statusFilter) return false;
          if (!search) return true;
          const q = search.toLowerCase();
          return (
               inq.name?.toLowerCase().includes(q) ||
               inq.email?.toLowerCase().includes(q) ||
               inq.phone?.includes(q)
          );
     });

     const inputClasses =
          'bg-white border border-gray-200 rounded-lg px-4 py-2 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300';

     if (loading) return <p className='text-darker/60'>Loading inquiries...</p>;

     return (
          <div>
               <div className='flex flex-col tablet:flex-row items-start tablet:items-center justify-between mb-6 gap-4'>
                    <h1 className='text-darker text-2xl font-bold'>Inquiries</h1>
                    <input
                         type='text'
                         value={search}
                         onChange={(e) => setSearch(e.target.value)}
                         placeholder='Search by name, email, or phone...'
                         className={inputClasses + ' w-full tablet:w-72'}
                    />
               </div>

               {/* Status Filter */}
               <div className='flex gap-2 mb-6 overflow-x-auto pb-2'>
                    {STATUSES.map((s) => (
                         <button
                              key={s}
                              onClick={() => setStatusFilter(s)}
                              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap duration-300 ${
                                   statusFilter === s
                                        ? 'bg-yellow/10 text-yellow'
                                        : 'text-darker/60 hover:text-darker hover:bg-gray-100'
                              }`}
                         >
                              {s === 'all' ? 'All' : STATUS_LABELS[s]}
                              {s !== 'all' && (
                                   <span className='ml-1 text-darker/30'>
                                        ({inquiries.filter((i) => i.status === s).length})
                                   </span>
                              )}
                         </button>
                    ))}
               </div>

               <div className='bg-white rounded-xl border border-gray-200 overflow-hidden'>
                    {filtered.length === 0 ? (
                         <p className='text-darker/40 p-4'>
                              {inquiries.length === 0
                                   ? 'No inquiries yet. Leads will appear here when submitted from the landing page.'
                                   : 'No inquiries match your filters.'}
                         </p>
                    ) : (
                         <div className='overflow-x-auto'>
                              <table className='w-full text-left'>
                                   <thead>
                                        <tr className='border-b border-gray-200 text-darker/40 text-sm'>
                                             <th className='p-4'>Name</th>
                                             <th className='p-4'>Phone</th>
                                             <th className='p-4'>Source</th>
                                             <th className='p-4'>Status</th>
                                             <th className='p-4'>Date</th>
                                             <th className='p-4'></th>
                                        </tr>
                                   </thead>
                                   <tbody className='divide-y divide-gray-200'>
                                        {filtered.map((inq) => (
                                             <tr key={inq.id} className='hover:bg-gray-100 duration-300'>
                                                  <td className='p-4'>
                                                       <p className='text-darker'>{inq.name}</p>
                                                       <p className='text-darker/40 text-sm'>{inq.email}</p>
                                                  </td>
                                                  <td className='p-4 text-darker/60 text-sm'>{inq.phone}</td>
                                                  <td className='p-4 text-darker/60 text-sm'>
                                                       {inq.utm_source || (inq.gclid ? 'Google Ads' : 'Direct')}
                                                  </td>
                                                  <td className='p-4'>
                                                       <span
                                                            className={`px-2 py-1 rounded text-xs ${
                                                                 STATUS_COLORS[inq.status] || 'bg-gray-50 text-darker/40'
                                                            }`}
                                                       >
                                                            {STATUS_LABELS[inq.status] || inq.status}
                                                       </span>
                                                  </td>
                                                  <td className='p-4 text-darker/40 text-sm whitespace-nowrap'>
                                                       {format(new Date(inq.created_at), 'MMM d, yyyy')}
                                                  </td>
                                                  <td className='p-4'>
                                                       <Link
                                                            to={`/admin/inquiries/${inq.id}`}
                                                            className='text-yellow text-sm hover:underline'
                                                       >
                                                            View &rarr;
                                                       </Link>
                                                  </td>
                                             </tr>
                                        ))}
                                   </tbody>
                              </table>
                         </div>
                    )}
               </div>
          </div>
     );
}
